import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { findMood, type MoodKey } from '@qq/shared';
import { api } from '../lib/api.js';
import { MoodFace } from '../art/MoodFace.js';

interface CheckIn {
  id: string;
  learningDay: string;
  mood: MoodKey;
  note: string | null;
  createdAt: string;
  updatedAt: string;
  seenByParent: boolean;
  flaggedTerms: string[];
}

interface ChildCheckIns {
  childId: string;
  nickname: string;
  unseen: number;
  surfacing: { urgency: 'routine' | 'today' | 'soon'; reasons: string[]; flagged: string[] };
  checkIns: CheckIn[];
}

/**
 * The check-in screen for a parent.
 *
 * The child's words appear verbatim and in full. Nothing here summarises them,
 * scores them, or tells a parent what their child is feeling - the app has no
 * honest way to know that, and a confident guess would be acted on as though it
 * were a finding.
 *
 * What it does do is order and time: a flagged note is put at the top today
 * rather than in Sunday's email. It never hides anything.
 */
export function AdminCheckIns() {
  const queryClient = useQueryClient();
  const data = useQuery({
    queryKey: ['admin', 'checkins'],
    queryFn: () => api<{ children: ChildCheckIns[] }>('/admin/checkins?days=60'),
  });

  const markSeen = useMutation({
    mutationFn: (childId: string) => api(`/admin/checkins/${childId}/seen`, { method: 'POST' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'checkins'] }),
  });

  return (
    <main className="screen admin">
      <h1>Check-ins</h1>
      <p className="muted">
        What each child tapped, and anything they wrote, exactly as they wrote it. Nothing on this
        page is interpreted, scored or summarised &mdash; there is no honest way for an app to do
        that, so it does not try.
      </p>

      {data.isPending && <p>Loading&hellip;</p>}

      {data.data?.children.map((child) => (
        <section key={child.childId} className="card">
          <h2>
            {child.nickname}
            {child.unseen > 0 && <span className="pill">{child.unseen} new</span>}
          </h2>

          {child.surfacing.reasons.length > 0 && (
            <div
              className={
                child.surfacing.urgency === 'today' ? 'surfacing surfacing--today' : 'surfacing'
              }
            >
              {child.surfacing.reasons.map((reason) => (
                <p key={reason}>{reason}</p>
              ))}
              {child.surfacing.urgency === 'today' && (
                <p className="muted">
                  If you want somebody to talk to about this, Kids Helpline is free, 24/7, for ages
                  5&ndash;25: <strong>1800 55 1800</strong>. In an emergency, call 000.
                </p>
              )}
            </div>
          )}

          {child.checkIns.length === 0 && (
            <p className="muted">
              No check-ins yet. {child.nickname} is asked once a day, before the adventure starts.
            </p>
          )}

          <ul className="checkin-list">
            {child.checkIns.map((entry) => {
              const mood = findMood(entry.mood);
              return (
                <li key={entry.id} className={entry.seenByParent ? '' : 'checkin-row--new'}>
                  <MoodFace mood={mood?.face ?? 'ok'} size={44} title={mood?.parentLabel} />
                  <div>
                    <strong>{entry.learningDay}</strong> &mdash; {mood?.parentLabel ?? entry.mood}
                    {entry.note && <blockquote className="child-words">{entry.note}</blockquote>}
                    {entry.flaggedTerms.length > 0 && (
                      <p className="muted">
                        Brought forward because these words appear: {entry.flaggedTerms.join(', ')}.
                        That is a literal text match, not a judgement about what was meant.
                      </p>
                    )}
                    {entry.updatedAt !== entry.createdAt && (
                      <p className="muted">{child.nickname} changed this later in the day.</p>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>

          {child.unseen > 0 && (
            <button
              type="button"
              className="btn btn--quiet"
              disabled={markSeen.isPending}
              onClick={() => markSeen.mutate(child.childId)}
            >
              Mark as read
            </button>
          )}
        </section>
      ))}

      <section className="card card--soft">
        <h2>How this works</h2>
        <ul>
          <li>
            Each child answers once a day, before their adventure starts. It cannot be skipped.
            Answering in the morning does not mean being asked again after lunch.
          </li>
          <li>
            <strong>The screen does not tell them you read this.</strong> That is your call, made so
            the answers stay unguarded. Worth knowing what it costs: the first time you act on
            something you read here, they will work out that it was read. Telling them yourself,
            once, in person keeps the screen clean and keeps them informed &mdash; the choice is
            yours, and nothing in the app depends on it.
          </li>
          <li>
            Nothing is earned for checking in, and nothing for any particular answer. Rewarding
            cheerful answers would teach them to give you the cheerful one.
          </li>
          <li>
            Two or more low days in a row gets raised here. Certain words in a note get raised the
            same day. Both are simple rules you can read, not a model&rsquo;s opinion.
          </li>
        </ul>
        <p>
          <Link to="/admin/writing">Their longer writing is on the writing screen.</Link>
        </p>
      </section>
    </main>
  );
}
